export default Header;
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { twMerge } from 'tailwind-merge';
import { Config } from 'vike-react/Config';

import { wrapPageTitle } from '@/renderer/getPageTitle';
import { goBackInApp } from '@/renderer/utils/inAppHistory';
import { isStaffRole } from '@/renderer/utils/isStaffRole';
import { SafeTopSpacer } from '@/renderer/utils/safeTop';
import useMe from '@/renderer/utils/useMe';
import { usePageContext } from '@/renderer/utils/usePageContext';

/**
 * The phone tab bar's destinations (components/Nav/MobileTabBar.tsx), plus the
 * signed-out Results tab. These are where you navigate FROM, so their headers
 * carry no back chevron; every other page with the shared header does (#2142).
 * The third tab is Admin for staff and Order for everyone else, so for staff
 * /order (reached from their More sheet) gets a chevron and /admin does not.
 */
const TAB_ROOTS = ['/release-center', '/results', '/support-chat', '/archive'];

export function showsBackChevron(urlPathname: string, isStaff = false) {
  if (TAB_ROOTS.includes(urlPathname) || urlPathname.startsWith('/results/')) {
    return false;
  }
  return urlPathname !== (isStaff ? '/admin' : '/order');
}

/**
 * Phones only, and only where the bottom tab bar is mounted: the CSS rule on
 * `[data-back-chevron]` in renderer/styles/index.css keys it to `html[data-tabbar]`
 * below `md`, so desktop (sidebar) and the standalone pages without a tab bar
 * are unchanged. Goes back in history, or to the release center when the page
 * was opened directly (renderer/utils/inAppHistory.ts).
 */
function BackChevron() {
  const { urlPathname } = usePageContext();
  const me = useMe();

  if (!showsBackChevron(urlPathname, isStaffRole(me?.role))) {
    return null;
  }

  return (
    <button
      type="button"
      data-back-chevron=""
      aria-label="Back"
      onClick={() => goBackInApp()}
      className="-ml-2 mr-1 size-9 shrink-0 items-center justify-center rounded-full text-zinc-700 active:bg-zinc-900/5 dark:text-zinc-300 dark:active:bg-white/10"
    >
      <ChevronLeftIcon className="size-5" strokeWidth={2.25} aria-hidden="true" />
    </button>
  );
}

function Header({
  title,
  pageTitle,
  Icon: _Icon,
  className,
  children,
  as = 'h2',
}: {
  title: string | JSX.Element | JSX.Element[];
  pageTitle?: string;
  Icon?: React.ForwardRefExoticComponent<React.PropsWithoutRef<React.SVGProps<SVGSVGElement>>>;
  className?: string;
  children?: JSX.Element | JSX.Element[];
  as?: React.ElementType;
}) {
  const As = as;

  return (
    // Shared page header, and the sole sticky top bar for every content page
    // (#1572): `sticky top-0` pins it to whichever scroll ancestor the page
    // provides (e.g. the inner overflow-auto container on results), so the title
    // stays on screen while content scrolls under the opaque bg-[Canvas] bar.
    <header
      className={twMerge(
        'sticky top-0 z-30 shrink-0 border-zinc-900/10 border-b bg-[Canvas] p-4 py-0 shadow-xs md:px-6 dark:border-white/10',
        className,
      )}
    >
      <Config title={wrapPageTitle(pageTitle || title)} />
      {/* Top safe-area inset (iOS notch / status bar). The native shell paints
          the page edge-to-edge (`viewport-fit=cover`, capacitor `contentInset:
          'never'`), so this bar — the sole sticky top chrome — carries the inset
          itself: a spacer INSIDE the opaque header, so the status-bar band is
          filled by the header background and content scrolls under it, not
          behind the status bar. A spacer (not padding) so callers' `py-*`
          overrides can't drop it. env() is 0 on web / non-notch, so nothing
          changes there (#2021).

          The spacer also registers this bar as the page's inset owner, so the
          layout (<main>) adds no status-bar padding/backdrop of its own — it
          only does that for views with no such owner (Header-less pages, #2028);
          see renderer/utils/safeTop.tsx. */}
      <SafeTopSpacer />
      <div className="flex flex-row items-center justify-between space-x-2">
        {/* No hamburger: below `md` the bottom tab bar is the primary navigation
            and the sidebar is desktop-only (#2142). */}
        <div className="flex w-full flex-row max-md:min-h-9 items-center md:space-x-4">
          <BackChevron />
          {/* {Icon && <Icon className="hidden h-6 w-auto shrink-0 text-zinc-500 sm:hidden" />} */}
          <div className="flex flex-1 flex-row items-center justify-between">
            <As className="m-0 select-none font-semibold text-base text-zinc-800 leading-7 dark:text-zinc-300">
              {title}
            </As>
            {children}
          </div>
        </div>
      </div>
    </header>
  );
}
